/**
 * GET /dashboard/action-queue (api#1116) — the Action Queue's items from every
 * vertical the company has EXCEPT casing, whose items are still built here from
 * the casing reads (buildActionAlerts). snake_case, as the route sends it.
 *
 * Three facts this shape carries that a list alone would not:
 *
 *   - `checked` names every item kind that ran for this caller. Empty `items`
 *     is "nothing needs action" only when `checked` is non-empty and
 *     `failures` is empty.
 *   - `failures` is an item whose query failed. It is not an item with a
 *     count of zero, and it is never dropped.
 *   - `amounts` is one entry per currency, never summed across them, and
 *     covers only the rows that carry a figure. `unpriced_count` is how many
 *     did not — so a total is never read as the whole, and never as $0.
 */
export type ActionQueueVertical =
  'downhole_tools' | 'trucking' | 'surface_rentals' | 'pressure_testing' | 'ndt_inspection' | 'pipeline_facility' | 'well_testing' | 'well_servicing';

/**
 * The verticals for which the Overview ASKS the route. `ndt_inspection` is
 * here so an NDT-only tenant asks at all (api#1142). The API gates NDT items
 * on the company's NDT DATA, not on this key — a company holding NDT rows
 * without the vertical would get them too — but the frontend cannot know
 * about data without asking, and a casing-only tenant (Smith) must keep
 * making no request. So the ask is vertical-gated and the answer data-gated.
 */
export const ACTION_QUEUE_VERTICALS: readonly ActionQueueVertical[] = [
  'downhole_tools', 'trucking', 'surface_rentals', 'pressure_testing', 'ndt_inspection',
  // PF-6 (fieldiq-api#1236): tickets to approve / send / invoice, overdue
  // invoices, AI drafts to review. Vertical-gated on the API too.
  'pipeline_facility',
  // WT-6 (fieldiq-api#1261): unpriced drafts, drafts to approve, sent-not-signed,
  // unacknowledged flags, AI drafts to review. Vertical-gated on the API too.
  'well_testing',
  // WS-6 (fieldiq-api#1296): sheets to approve, tickets to approve / send /
  // invoice, overdue invoices, BOP attention, AI drafts to review.
  'well_servicing',
];

export type ActionQueueSeverity = 'error' | 'warn' | 'info';

export interface ActionQueueAmount {
  currency: string;
  amount: number;
}

export interface ActionQueueItem {
  vertical: ActionQueueVertical;
  kind: string;
  severity: ActionQueueSeverity;
  /** Pluralised by the server from `count`. Rendered as-is. */
  label: string;
  count: number;
  amounts: ActionQueueAmount[];
  unpriced_count: number;
  /** The frontend route that lists these rows. */
  link: string;
}

export interface ActionQueueFailure {
  vertical: ActionQueueVertical;
  kind: string;
  error: string;
}

export interface ActionQueue {
  today: string;
  time_zone: string;
  verticals: ActionQueueVertical[];
  checked: string[];
  items: ActionQueueItem[];
  failures: ActionQueueFailure[];
}
